import React from 'react';
import { ToxnotServiceClient } from '../../../../store/ToxnotServiceClient';
import { ToxnotReactModal, ToxnotReactModalBody } from '../../ToxnotReactModal';
import { hidePopoverAndBlur, useBootstrapPopover } from '../hooks/useBootstrapPopover';
import { useLocalizer } from '../hooks/useLocalizer';

const acceptConfirmationModalId = 'ai-gap-summary-accept-confirmation-modal';
const acceptStartedModalId = 'ai-gap-summary-accept-started-modal';

interface AcceptAndReviewButtonProps {
    requestId: string;
    setErrorMessage: (message: string | null) => void;
}

export const AcceptAndReviewButton: React.FC<AcceptAndReviewButtonProps> = ({
    requestId,
    setErrorMessage
}) => {
    const localize = useLocalizer();
    const serviceClient = React.useMemo(() => new ToxnotServiceClient(), []);
    const [isAccepting, setIsAccepting] = React.useState(false);
    const title = localize('Accept & review in library');
    const description = localize('Import answered questions now. Unanswered questions remain blank for manual review in the material library.');
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);

    useBootstrapPopover(buttonRef, isAccepting);

    const showModal = (modalId: string) => {
        // Static backdrop + no keyboard dismiss: the accept-started modal must not be dismissible any way
        // other than its OK button (which triggers the reload), and the confirmation modal must stay put
        // while an accept is in flight so a sibling action cannot be started behind it.
        $(`#${modalId}`).modal({ backdrop: 'static', keyboard: false });
    };

    const hideModal = (modalId: string) => {
        $(`#${modalId}`).modal('hide');
    };

    const openAcceptConfirmation = () => {
        hidePopoverAndBlur(buttonRef);
        setErrorMessage(null);
        showModal(acceptConfirmationModalId);
    };

    const acceptAndReview = async () => {
        setIsAccepting(true);
        setErrorMessage(null);

        try {
            const apiResult = await serviceClient.acceptOfflineAiGapSummaryResponse(requestId);

            if (apiResult.success && apiResult.data) {
                setIsAccepting(false);
                hideModal(acceptConfirmationModalId);

                if (apiResult.data.acceptStarted) {
                    showModal(acceptStartedModalId);
                } else {
                    // Nothing new was queued (already accepted / nothing pending) - go straight to review.
                    window.location.reload();
                }

                return;
            }
        } catch {
            // Fall through to shared error handling below.
        }

        setIsAccepting(false);
        hideModal(acceptConfirmationModalId);
        setErrorMessage(localize('Sorry, there was a problem accepting this supplier response. Please try again.'));
    };

    const acceptStartedOk_click = () => {
        hideModal(acceptStartedModalId);
        window.location.reload();
    };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                className="gap-summary-resolution-option gap-summary-resolution-primary"
                data-toggle={isAccepting ? undefined : 'popover'}
                data-container={isAccepting ? undefined : 'body'}
                data-placement={isAccepting ? undefined : 'bottom'}
                data-content={isAccepting ? undefined : description}
                aria-label={`${title}. ${description}`}
                disabled={isAccepting}
                onClick={openAcceptConfirmation}
            >
                <span className="gap-summary-resolution-icon">
                    <span className="glyphicon glyphicon-stats" aria-hidden="true"></span>
                </span>
                <span className="gap-summary-resolution-copy">
                    <strong>{title}</strong>
                </span>
                <span className="glyphicon glyphicon-arrow-right gap-summary-resolution-arrow" aria-hidden="true"></span>
            </button>

            <ToxnotReactModal
                modalId={acceptConfirmationModalId}
                localizedTitle={localize('Accept AI response?')}
            >
                <ToxnotReactModalBody>
                    <p>
                        {localize('Are you sure you want to accept this AI response? Answered questions will be imported now, and unanswered required questions will remain blank for manual review.')}
                    </p>
                </ToxnotReactModalBody>
                <div className="modal-footer">
                    <button type="button" className="btn btn-default" data-dismiss="modal" disabled={isAccepting}>
                        {localize('Cancel')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-toxnot-primary"
                        disabled={isAccepting}
                        onClick={acceptAndReview}
                    >
                        {localize('Accept Response')}
                    </button>
                </div>
            </ToxnotReactModal>

            <ToxnotReactModal
                modalId={acceptStartedModalId}
                customHeader={<h4 className="modal-title">{localize('Accept response started')}</h4>}
            >
                <ToxnotReactModalBody>
                    <p>
                        {localize('The accept job has been triggered and will continue in the background. The page will refresh when you click OK.')}
                    </p>
                </ToxnotReactModalBody>
                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-toxnot-primary"
                        onClick={acceptStartedOk_click}
                    >
                        {localize('OK')}
                    </button>
                </div>
            </ToxnotReactModal>
        </>
    );
};

export default AcceptAndReviewButton;
